import * as THREE from 'three'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import BaseLineTool from '@glass-designer/tools/action/baseLineTool'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'


// Skew tool
// Tool to skew a rectangle shape into a parallelogram
export default class SkewTool extends BaseLineTool {
  constructor(editor) {
    super(editor)

    this.type = GlassDesignerTypes.ToolTypes.skewTool
    this.requiredDiagonalLength = 0

    this.editingLine.setShowOutsideTextLabel(false)
    this.editingLine.setShowInsideTextLabel(false)
    this.editingLine.setUseAngles(false)

    this.traceOptions.ignoreSplitLines = true
    this.snapOptions.ignoreDatumLines = true
    this.snapOptions.ignoreSegmentEdges = true
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive(options)

    // Start point selections
    this.resetSkewLine()

    this.displayToolSettingsPopover()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.editor.endShapeEditingMode()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    this.editingLine.setVisible(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected)
  }

  // Reset the skew line process
  resetSkewLine() {
    this.requiredDiagonalLength = 0

    this.editor.component.showToolHelpBox('fa-square icon-skew-qtr', 'Click and place the start of the skew line')
    this.resetLineDetails()

    this.editor.endShapeEditingMode()

    this.editor.flagRenderRequired()
  }

  // Display the tool settings popover for this tool
  displayToolSettingsPopover() {
    var lineLength = 0
    var skewAvailable = false

    if(this.lineDetails.startPointSelected && this.lineDetails.endPointSelected) {
      skewAvailable = true

      lineLength = MathsUtils.roundNumber(this.editingLine.startPoint.distanceTo(this.editingLine.endPoint), 0)
      if(this.requiredDiagonalLength == 0)
        this.requiredDiagonalLength = lineLength
    }

    this.editor.component.displaySkewToolPopover({
      skewData: {
        lineLength: lineLength,
        requiredDiagonalLength: this.requiredDiagonalLength,
        skewAvailable: skewAvailable
      },
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      updateCallback: (ctx, options) => {
        ctx.requiredDiagonalLength = options.requiredDiagonalLength

        // Update skew display
        ctx.updateSkew()
      },
      actionCallback: (ctx, tag, info) => {
        if(tag == 'applySkew') {
          ctx.requiredDiagonalLength = info.requiredDiagonalLength
          ctx.applySkew()
        }
        else if(tag == 'reset') {
          ctx.resetSkewLine()
          ctx.displayToolSettingsPopover()
        }        
      }
    })
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    super.onMouseDownEvent(ev, mouseData)
  }
  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)
    if(this.lineDetails.settingEndPoint && ev.button == Config.Mouse.buttonSelect) {
      this._updateSkewLineEndpoint(mouseData)
    }
  }
  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)

    if(ev.button == Config.Mouse.buttonSelect) {
      if(this._updateSkewLinePoints(mouseData)) {
        this.editor.flagRenderRequired()
        return true
      }
    }

    return false
  }
  onMouseWheelEvent(ev, mouseData) {
    super.onMouseWheelEvent(ev, mouseData)
  }
  onTouchStartEvent(ev, touchData) {
    super.onTouchStartEvent(ev, touchData)
  }
  onTouchEndEvent(ev, touchData) {
    super.onTouchEndEvent(ev, touchData)
    if(this._updateSkewLinePoints(touchData)) {
      this.editor.flagRenderRequired()
      return true
    }
    return false
  }
  onTouchCancelEvent(ev, touchData) {
    super.onTouchCancelEvent(ev, touchData)
  }
  onTouchMoveEvent(ev, touchData) {
    super.onTouchMoveEvent(ev, touchData)
    if(this.lineDetails.settingEndPoint) {
      this._updateSkewLineEndpoint(touchData)
    }
  }


  /**
   * Update the skew line endpoint
   * Done during mouse move / touch move
   */
  _updateSkewLineEndpoint(mouseTouchData) {
    if(!this.selectedShape)
      return
      
    this.lineDetails.endPointSelected = true
    
    var nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y)) 

    this.lineDetails.end = nearestPoint

    // Update editing line
    this.editingLine.setStartPoint(this.lineDetails.start.clone())
    this.editingLine.setEndPoint(this.lineDetails.end.clone())    
    this.editingLine.startAngleFixed = this.lineDetails.startAngleFixed
    this.editingLine.endAngleFixed = this.lineDetails.endAngleFixed

    this.editor.flagRenderRequired()
  }


  /**
   * Update the skew line start/end points from mouse/touch event
   * Returns true if the skew line was updated & the mouse/touch event was processed
   */
  _updateSkewLinePoints(mouseTouchData) {
    if(!this.lineDetails.startPointSelected) {
      if(mouseTouchData.isSnapGridEnabled)
        this.lineDetails.start.set(mouseTouchData.snappedPosition.x, mouseTouchData.snappedPosition.y)
      else
        this.lineDetails.start.set(mouseTouchData.position.x, mouseTouchData.position.y)

      // Get shape closest to the skew line
      let closestShape = this.editor.findClosestGlassShape(this.lineDetails.start)
      if(!closestShape)
        return

      // Validate if the shape is available for skewing
      if(!this.checkValidShape(closestShape)) {
        this.editor.component.errorMessage('Invalid panel', 'Panel must have 4 sides and no arcs for skewing')
        return false
      }

      this.selectedShape = closestShape
      this.selectedShapeBounds = this.selectedShape.calculateSegmentBounds()
      this.selectedShapeBounds.getCenter(this.selectedShapeCenter)

      this.lineDetails.startPointSelected = true
      this.lineDetails.settingEndPoint = true

      // Attempt to snap to the nearest vertex on the shape
      let nearestPoint = this.snapToNearestShapePoint(this.selectedShape, new THREE.Vector2(mouseTouchData.position.x, mouseTouchData.position.y))
      this.lineDetails.start = nearestPoint

      // Disable the editing line from touch/mouse events to stop it receiving clicks
      this.editingLine.setEnabled(false)

      this.editor.component.showToolHelpBox('fa-square icon-skew-qtr', 'Click on the opposing corner of the rectangle')
      return true
    }

    if(this.lineDetails.settingEndPoint) {
      this._updateSkewLineEndpoint(mouseTouchData)

      this.editor.component.showToolHelpBox('fa-square icon-skew-qtr', 'Apply skew or reset skew line')
      this.lineDetails.settingEndPoint = false

      // Enable the editing line now it's placed
      this.editingLine.setEnabled(true)

      // Set the skew line to match the final traced values
      this.updateSkewLineToTracedLine()
      this.updateEditingLineLinkedObjects()

      // Setup dimensions for the selected shape & editing line
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      this.editor.startShapeEditingMode(this.selectedShape)

      // Update settings popover
      this.displayToolSettingsPopover()

      // Update skew display
      this.updateSkew()

      return true
    }
  }

  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      if(object == this.editingLine.startHandle) {

        this.lineDetails.start.set(this.editingLine.startHandle.position.x, this.editingLine.startHandle.position.y, this.lineDetails.start.z)

        // Snap skew start point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.startHandle.position)

        this.lineDetails.start.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setStartPoint(this.lineDetails.start)
        this.updateEditingLineLinkedObjects()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])
        
        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine.endHandle) {

        this.lineDetails.end.set(this.editingLine.endHandle.position.x, this.editingLine.endHandle.position.y, this.lineDetails.end.z)

        // Snap skew end point to nearest part of the shape
        let closestPoint = this.snapToNearestShapePoint(this.selectedShape, this.editingLine.endHandle.position)

        this.lineDetails.end.set(closestPoint.x, closestPoint.y, this.lineDetails.end.z)

        this.editingLine.setEndPoint(this.lineDetails.end)
        this.updateEditingLineLinkedObjects()
        
        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }
      else if(object == this.editingLine) {
        this.lineDetails.start.set(this.lineDetails.start.x + data.moveOffset.x, this.lineDetails.start.y + data.moveOffset.y, this.lineDetails.start.z)
        this.lineDetails.end.set(this.lineDetails.end.x + data.moveOffset.x, this.lineDetails.end.y + data.moveOffset.y, this.lineDetails.end.z)

        // Run trace when moving the editing line
        this.traceEditingLine({
          updateStartPoint: true,
          updateEndPoint: true,
          referencePoint: data.grabPoint
        })

        this.updateEditingLineLinkedObjects()

        // Setup dimensions for the selected shape & editing line
        this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

        this.editor.flagRenderRequired()
      }

    }

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectReleased) {

      // Set the skew line to match the final traced values
      this.updateSkewLineToTracedLine()

      // Update dimensions after moving objects
      this.editor.onToolEditingUpdated(this.selectedShape, [this.editingLine])

      // Update info on popover
      this.displayToolSettingsPopover()

      // Update skew display
      this.updateSkew()
    }
  }


  /**
   * Update the skew line settings to match the final raytraced points
   */
  updateSkewLineToTracedLine() {
    this.lineDetails.start = this.editingLine.startPoint.clone()
    this.lineDetails.end = this.editingLine.endPoint.clone()
  }


  /**
   * Check if a shape is a valid shape for skewing
   * Shape must be 4 sided & contain no arcs (ie, all lines)
   */
  checkValidShape(shape) {
    if(!shape)
      return false

    if(shape.segmentList.length != 4)
      return false

    let arcCount = shape.segmentList.filter(segment => segment.type == GlassDesignerTypes.SegmentTypes.arc).length
    if(arcCount > 0)
      return false

    return true
  }


  /**
   * Update the skew on the highlighted shape during editing
   */
  updateSkew() {
    try {
      this._runSkewUpdate(false, this.requiredDiagonalLength)

    } catch(err) {
      this.editor.component.updatePopoverErrorText(err.message)      
    }
  }


  /**
   * Apply the skew function to the selected shape & finish the tool
   */
  applySkew() {
    this.editor.startUndoGroup('Skew Shape')
    this.editor.addUndoShape(this.selectedShape)

    try {
      this._runSkewUpdate(true, this.requiredDiagonalLength)

    } catch(err) {
      // Undo anything done by the failed skew shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error skewing panel', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(this.selectedShape)

    this.closeTool()
  }


  /**
   * Update the skew to the shape
   */
   _runSkewUpdate(applyShape, diagonalLength) {
    if(!this.selectedShape)
      return

    var updateShape = this.selectedShape

    if(!applyShape) {
      this.editor.resetHighlightShape(this.selectedShape)
      updateShape = this.editor.highlightShape
    }

    var startSegmentIndex = updateShape.calculateClosestSegmentStartToPoint(this.lineDetails.start)
    var endSegmentIndex = updateShape.calculateClosestSegmentStartToPoint(this.lineDetails.end)

    // Must be at opposing ends of the 4 sided shape
    if(startSegmentIndex == -1 || endSegmentIndex == -1)
      throw new EditorUserError('Invalid selection points')
    if(Math.abs(endSegmentIndex - startSegmentIndex) != 2)
      throw new EditorUserError('Invalid selection points')

    let lengthA = updateShape.getSegmentForIndex(startSegmentIndex).getLength()
    let lengthB = updateShape.getSegmentForIndex(startSegmentIndex + 1).getLength()
    let lengthC = updateShape.getSegmentForIndex(endSegmentIndex).getLength()
    let lengthD = updateShape.getSegmentForIndex(endSegmentIndex+1).getLength()

    // Triangle formula
    var tri1Y = (diagonalLength*diagonalLength + lengthA*lengthA - lengthB*lengthB) / (2*diagonalLength)
    var tri1X = Math.sqrt(lengthA*lengthA - tri1Y*tri1Y)

    var tri2Y = (diagonalLength*diagonalLength + lengthD*lengthD - lengthC*lengthC) / (2*diagonalLength)
    var tri2X = Math.sqrt(lengthD*lengthD - tri2Y*tri2Y)

    if(tri1X == 0.0 || isNaN(tri1X) || tri2X == 0.0 || isNaN(tri2X)) {
      // No solution
      // Update so it shows reset shape
      updateShape.segmentListUpdated()
      updateShape.updateSceneObject()
      this.editor.render()

      throw new EditorUserError('Cannot apply skew for the length')
    }

    let dir = updateShape.segmentList[endSegmentIndex].startPosition.clone().sub(updateShape.segmentList[startSegmentIndex].startPosition)
    dir.normalize()
    let rightDir = new THREE.Vector2(dir.y, -dir.x)

    let posA = updateShape.segmentList[startSegmentIndex].startPosition.clone()
    posA.addScaledVector(dir, tri1Y)
    posA.addScaledVector(rightDir, tri1X)

    let posB = updateShape.segmentList[startSegmentIndex].startPosition.clone()
    posB.addScaledVector(dir, tri2Y)
    posB.addScaledVector(rightDir, -tri2X)

    let endPos = updateShape.segmentList[startSegmentIndex].startPosition.clone()
    endPos.addScaledVector(dir, diagonalLength)

    updateShape.segmentList[startSegmentIndex].endPosition.copy(posA)
    updateShape.getSegmentForIndex(startSegmentIndex+1).startPosition.copy(posA)

    updateShape.getSegmentForIndex(startSegmentIndex+1).endPosition.copy(endPos)
    updateShape.segmentList[endSegmentIndex].startPosition.copy(endPos)

    updateShape.segmentList[endSegmentIndex].endPosition.copy(posB)
    updateShape.getSegmentForIndex(endSegmentIndex+1).startPosition.copy(posB)


    updateShape.segmentListUpdated()
    updateShape.updateSceneObject()
    this.editor.render()
  }

}
